import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Check, ArrowLeft, Shield, Zap, Star, Sparkles, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ShopifyBuyButton from '../components/ShopifyBuyButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

export default function ProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-28">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-28">
        <h1 className="font-serif text-3xl text-white mb-4">Product Not Found</h1>
        <Link to="/products">
          <Button variant="outline" className="rounded-none">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Button>
        </Link>
      </div>
    );
  }

  const isSubscription = product.type === 'subscription';
  const Icon = isSubscription ? Users : Sparkles;

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <Link 
          to="/products" 
          className="inline-flex items-center text-sm text-[#999999] hover:text-white transition-colors"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Link>
      </div>

      {/* Product Section */}
      <section className="py-8 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center"
          >
            {/* Icon */}
            <motion.div variants={fadeInUp} className="mb-8">
              <div className={`w-20 h-20 mx-auto flex items-center justify-center ${isSubscription ? 'bg-[#D4AF37]/10' : 'bg-[#0f0f0f]'}`}>
                <Icon className={`w-10 h-10 ${isSubscription ? 'text-[#D4AF37]' : 'text-[#D4AF37]'}`} />
              </div>
            </motion.div>

            {/* Type badge */}
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              {isSubscription ? 'Monthly Program' : 'Digital Guide'}
            </motion.p>

            {/* Title */}
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6"
              data-testid="product-name"
            >
              {product.name}
            </motion.h1>

            {/* Description */}
            <motion.p variants={fadeInUp} className="text-lg text-[#999999] leading-relaxed mb-8 max-w-2xl mx-auto">
              {product.description}
            </motion.p>

            {/* Price */}
            <motion.div variants={fadeInUp} className="mb-10">
              <span 
                className="font-serif text-5xl text-white"
                data-testid="product-price"
              >
                €{product.price.toFixed(2)}
              </span>
              {isSubscription && (
                <span className="text-xl text-[#999999] ml-2">/month</span>
              )}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="max-w-md mx-auto mb-12">
              {product.id === 'signal-full-guide' ? (
                <ShopifyBuyButton productId="signal-full-guide" variant="primary" />
              ) : (
                <div className="space-y-3">
                  <Button 
                    disabled
                    className="w-full btn-premium bg-[#D4AF37]/50 text-white rounded-none py-4 text-sm uppercase tracking-widest font-semibold cursor-not-allowed"
                    data-testid="add-to-cart-button"
                  >
                    Coming Soon
                  </Button>
                  <p className="text-xs text-[#999999] text-center">
                    Contact via TikTok for coaching inquiries
                  </p>
                </div>
              )}
            </motion.div>

            {/* Trust badges */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-8 justify-center mb-16">
              <div className="flex items-center gap-2 text-sm text-[#999999]">
                <Shield className="w-5 h-5 text-[#D4AF37]" />
                Secure Payment
              </div>
              <div className="flex items-center gap-2 text-sm text-[#999999]">
                <Zap className="w-5 h-5 text-[#D4AF37]" />
                Instant Access
              </div>
              <div className="flex items-center gap-2 text-sm text-[#999999]">
                <Star className="w-5 h-5 text-[#D4AF37]" />
                Premium Quality
              </div>
            </motion.div>
          </motion.div>

          {/* What's Included */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="bg-[#0f0f0f] border border-[#1a1a1a] p-8 md:p-12"
          >
            <motion.h3 variants={fadeInUp} className="font-serif text-2xl text-white mb-8 text-center">
              What's Included
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.benefits?.map((benefit, idx) => (
                <motion.div 
                  key={idx}
                  variants={fadeInUp}
                  className="flex items-start gap-4 p-4 bg-[#0f0f0f]"
                >
                  <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-white">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 md:py-28 bg-[#0f0f0f]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              Questions?
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white mb-6">
              We've got answers
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#999999] mb-8">
              Check our FAQ for common questions about the program.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/faq">
                <Button 
                  variant="outline"
                  className="btn-premium border-[#1A1A1A] text-white hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-4 text-sm uppercase tracking-widest"
                  data-testid="faq-link"
                >
                  View FAQ
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
